import { useEffect, useMemo } from 'react';

import { selectedNoteIdAtom } from '@repo/videa-notes';
import { useAtom } from 'jotai';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useNoteById } from '@/react-query/videa-notes/useNoteById';

interface UseNoteSelectionProps {
  clinicalNotesData: ClinicalNote[];
}

/**
 * Manages note selection state and ensures full note data is available when needed.
 *
 * This hook handles:
 * - Tracking the currently selected note ID (stored in atom for persistence)
 * - Finding the selected note from the clinical notes list
 * - Auto-selecting the newest note if no note is currently selected
 * - Fetching full note data (with template/field data) when the selected note
 *   doesn't already have template data in the list
 * - Combining list data with fetched full data to provide complete note information
 *
 * Returns the selected note ID, setter, selected note object, and the note with
 * full template data (if fetched).
 */
export const useNoteSelection = ({
  clinicalNotesData,
}: UseNoteSelectionProps) => {
  const [selectedNoteId, setSelectedNoteId] = useAtom(selectedNoteIdAtom);

  const selectedNote = useMemo(() => {
    if (!selectedNoteId || !Array.isArray(clinicalNotesData)) return undefined;
    return clinicalNotesData.find((note) => note.id === selectedNoteId);
  }, [selectedNoteId, clinicalNotesData]);

  const hasTemplateData = useMemo(() => {
    return (
      selectedNote?.originalTemplate ||
      selectedNote?.editedTemplate ||
      selectedNote?.fields
    );
  }, [selectedNote]);

  const { data: fullNoteData, refetch: refetchFullNote } = useNoteById(
    selectedNote?.id,
    !!selectedNote?.id && !hasTemplateData,
  );

  const noteWithTemplateData = useMemo(() => {
    if (!selectedNote) return undefined;
    return fullNoteData || selectedNote;
  }, [selectedNote, fullNoteData]);

  useEffect(() => {
    if (!Array.isArray(clinicalNotesData) || clinicalNotesData.length === 0) {
      return;
    }

    if (!selectedNote) {
      const newestNote = clinicalNotesData.reduce((latest, current) =>
        new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest,
      );
      setSelectedNoteId(newestNote.id);
    }
  }, [clinicalNotesData, selectedNote, setSelectedNoteId]);

  return {
    selectedNoteId,
    setSelectedNoteId,
    selectedNote,
    noteWithTemplateData,
    refetchFullNote,
  };
};
